import type { HttpClient } from '../http.js';

/**
 * Tagada Rx — storefront module.
 *
 * Drives the post-checkout clinical case flow from the browser:
 *   1. Ask the API whether a product needs an Rx case.
 *   2. After the order is paid, submit the intake (patient + answers)
 *      with the `(orderId, checkoutToken)` ownership proof.
 *   3. Poll the case status from the thank-you page.
 *
 * PHI handling: the `patient` and `intakeAnswers` payloads are sent
 * straight to the Tagada API which forwards them to the clinical
 * network (e.g. MDI). Tagada NEVER persists PHI in its database.
 *
 * Auth model: there is no API key. Each route authorizes per-resource
 * (productId is public; case routes require the checkoutToken the
 * storefront already received when it bootstrapped the checkout).
 *
 * Rules: the server enforces the clinical network's contract on every
 * submit (US address and phone, required questions, offered options, MDI
 * demographics). Use `validateIntake` before payment and `isQuestionActive`
 * / `toIntakeAnswers` to render and send the questionnaire the same way.
 *
 * @example
 * ```ts
 * const { required } = await tagada.rx.isRequiredForProduct(productId);
 * if (required) {
 *   // Before payment: US address, phone, ZIP, age.
 *   const pre = await tagada.rx.validateIntake({ productId, patient });
 *   if (!pre.valid) return showErrors(pre.issues);
 *   // After payment: render the network questionnaire.
 *   const { questions } = await tagada.rx.getQuestionsForProduct(productId, variantId);
 *   const ctx = { state: patient.address.state, sexAtBirth: patient.sexAtBirth };
 *   const shown = questions.filter((q) => isQuestionActive(q, answers, ctx));
 *   // …collect `answers` keyed by externalQuestionId…
 *   const res = await tagada.rx.submitCase({
 *     orderId, checkoutToken, productId,
 *     patient, intakeAnswers: toIntakeAnswers(questions, answers, ctx),
 *   });
 *   router.push(`/thank-you?rxCase=${res.rxCaseId}`);
 * }
 * ```
 */
export type RxCaseStatus =
  | 'requested'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'shipped'
  | 'delivered'
  | 'declined'
  | 'cancelled'
  | 'error';

export interface RxRequiredForProduct {
  required: boolean;
  clinicalNetworkId: string | null;
  externalOfferingId: string | null;
  requiresLabs: boolean;
  requiresIdVerification: boolean;
  isDeaControlled: boolean;
}

export interface RxStorefrontPatient {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  /** ISO date YYYY-MM-DD. */
  dateOfBirth: string;
  sexAtBirth: 'male' | 'female' | 'unspecified';
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    /** ISO 3166-1 alpha-2 country code. */
    country: string;
  };
  /** Patient-level clinical fields (MDI Full API / Judd). */
  weight?: number | string;
  height?: number | string;
  allergies?: string;
  pregnancy?: boolean | string;
  currentMedications?: string;
  medicalConditions?: string;
  specialNecessities?: string;
  driverLicenseId?: string;
  introVideoId?: string;
}

export interface RxIntakeAnswer {
  questionId: string;
  value: string | number | boolean | string[];
  comment?: string;
  /** Exact question text shown to the patient (MDI case_questions.question). */
  question?: string;
  /** MDI answer type: string | number | boolean. */
  type?: string;
  important?: boolean;
  isCritical?: boolean;
  displayInPdf?: boolean;
  description?: string;
  label?: string;
  metadata?: string;
  /** Options presented for MCQ questions. */
  displayedOptions?: string[];
}

/** Display rule copied from the clinical network (see `isQuestionActive`). */
export interface RxClinicalQuestionRule {
  op: 'and' | 'or';
  requirements: Array<{
    /** `question`: answer to another question; `state`: shipping state; `other` + `patient_sex`: sex at birth. */
    basedOn: 'question' | 'state' | 'other';
    questionId: string | null;
    answer: string | null;
  }>;
}

export interface RxClinicalQuestion {
  externalQuestionId: string;
  title: string;
  description: string | null;
  placeholder: string | null;
  type: string;
  required: boolean;
  order: number;
  options: { value: string; label: string; important?: boolean; critical?: boolean }[];
  /** `false` = clinician-side question: never show it, never ask it. */
  visible?: boolean;
  /** Show the question only when one rule matches (`isQuestionActive`). */
  rules?: RxClinicalQuestionRule[];
  important?: boolean;
  critical?: boolean;
  label?: string | null;
  /** Value the network fills itself for hidden questions; send it as is. */
  defaultValue?: string | null;
}

/** Answers keyed by `externalQuestionId`, as a storefront collects them. */
export type RxQuestionAnswers = Record<string, string | number | boolean | string[] | undefined>;

export interface RxIntakeContext {
  /** Shipping state, 2-letter code. */
  state?: string;
  sexAtBirth?: 'male' | 'female' | 'unspecified';
}

const sameText = (a: unknown, b: string | null): boolean =>
  b !== null && String(a).trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Whether the patient must see (and answer) a question. Same rules the
 * server enforces on `submitCase`: hidden and file questions are never
 * shown; a question with rules shows when one rule matches the answers so
 * far, the shipping state or the patient's sex (e.g. "Drug Allergies" only
 * after "Yes", pregnancy only for women).
 */
export function isQuestionActive(
  question: RxClinicalQuestion,
  answers: RxQuestionAnswers,
  context: RxIntakeContext = {},
): boolean {
  if (question.visible === false || question.type === 'file') return false;
  const rules = question.rules ?? [];
  if (rules.length === 0) return true;
  return rules.some((rule) => {
    const check = (req: RxClinicalQuestionRule['requirements'][number]) => {
      if (req.basedOn === 'state') return sameText(context.state ?? '', req.answer);
      if (req.basedOn === 'other' && req.questionId === 'patient_sex') return sameText(context.sexAtBirth ?? '', req.answer);
      if (req.basedOn === 'question' && req.questionId) {
        const v = answers[req.questionId];
        if (v === undefined) return false;
        return Array.isArray(v) ? v.some((x) => sameText(x, req.answer)) : sameText(v, req.answer);
      }
      return false;
    };
    return rule.op === 'and' ? rule.requirements.every(check) : rule.requirements.some(check);
  });
}

/**
 * Turn the answers a storefront collected into the `intakeAnswers` to
 * submit: active questions only, keyed by title (what the clinician
 * reads), with the options that were shown, and the network's default for
 * hidden questions. Plug and play with `submitCase` / `validateIntake`.
 */
export function toIntakeAnswers(
  questions: RxClinicalQuestion[],
  answers: RxQuestionAnswers,
  context: RxIntakeContext = {},
): RxIntakeAnswer[] {
  const out: RxIntakeAnswer[] = [];
  for (const q of questions) {
    if (q.visible === false) {
      if (q.defaultValue) out.push({ questionId: q.title, question: q.title, value: q.defaultValue, type: 'string' });
      continue;
    }
    if (!isQuestionActive(q, answers, context)) continue;
    const value = answers[q.externalQuestionId];
    if (value === undefined || (typeof value === 'string' && value.trim() === '')) continue;
    out.push({
      questionId: q.title,
      question: q.title,
      value,
      type: q.type === 'number' ? 'number' : q.type === 'boolean' ? 'boolean' : 'string',
      ...(q.options.length > 0 ? { displayedOptions: q.options.map((o) => o.label) } : {}),
    });
  }
  return out;
}

export interface RxIntakeIssue {
  field: string;
  message: string;
}

export interface RxValidateIntakeInput {
  productId: string;
  variantId?: string;
  patient: {
    phone?: string | null;
    dateOfBirth: string;
    sexAtBirth: 'male' | 'female' | 'unspecified';
    address: { state: string; zip: string; country: string };
  };
  intakeAnswers?: RxIntakeAnswer[];
}

export interface RxQuestionsForProduct {
  required: boolean;
  externalQuestionnaireId: string | null;
  questions: RxClinicalQuestion[];
}

export interface RxSubmitCaseInput {
  orderId: string;
  checkoutToken: string;
  productId: string;
  patient: RxStorefrontPatient;
  intakeAnswers: RxIntakeAnswer[];
  preferredPharmacyId?: string;
}

export interface RxSubmitCaseResult {
  rxCaseId: string;
  externalCaseId: string;
  status: 'submitted' | 'error';
  accrualAmountMinor: number;
  accrualCurrency: string;
}

/** Basics Tagada already holds on the order. Never date of birth or sex at birth. */
export interface RxIntakeContextPatient {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
}

/**
 * What a thank-you page needs to resume an unfinished intake, or
 * `{ caseExists: true }` once a case is in (show its status instead).
 */
export type RxIntakeContextResult =
  | { caseExists: true }
  | {
      caseExists: false;
      orderId: string;
      storeId: string;
      productId: string;
      variantId: string | null;
      patient: RxIntakeContextPatient;
    };

export interface RxCaseSummary {
  id: string;
  status: RxCaseStatus;
  externalCaseId: string | null;
  lastShipmentShippedAt: string | null;
  createdAt: string;
}

export type RxActivationStepKey =
  | 'application'
  | 'approval'
  | 'payouts'
  | 'statement_descriptor'
  | 'storefront';

export type RxActivationMissingItem =
  | 'application'
  | 'approval'
  | 'payout_kyc'
  | 'payout_bank_account'
  | 'statement_descriptor'
  | 'statement_descriptor_rejected'
  | 'storefront'
  | 'partner_rx_option';

/**
 * Tagada Rx activation status of a store.
 *
 * `payments` is `'ready'` once the brand's Rx payment account is live and its
 * payment flow is bound; `'setting_up'` before. It is a status, not a step:
 * there is nothing for the brand to do while payments are being set up.
 * The public storefront route leaves out payout verification and partner
 * contract items (`payouts`, `payout_*`, `partner_rx_option`).
 */
export interface RxActivationStatus {
  storeId: string;
  state: 'not_requested' | 'pending_review' | 'active';
  steps: { key: RxActivationStepKey; done: boolean }[];
  missing: RxActivationMissingItem[];
  payments: 'setting_up' | 'ready';
  descriptorStatus: 'none' | 'pending' | 'approved' | 'rejected';
}

export class RxModule {
  constructor(
    private readonly http: HttpClient,
    private readonly storeId?: string,
  ) {}

  /**
   * Activation status of the store: steps, missing items and whether Rx
   * payments are `setting_up` or `ready`. Storefronts use it to show
   * "Payments are being set up" instead of letting a checkout fail.
   *
   * Read-only / public. Defaults to the client's `storeId`.
   */
  async getActivationStatus(storeId?: string): Promise<RxActivationStatus> {
    const id = storeId ?? this.storeId;
    if (!id) throw new Error('rx.getActivationStatus: storeId is required');
    return this.http.get<RxActivationStatus>('/api/public/v1/rx/activation-status', {
      params: { storeId: id },
    });
  }

  /**
   * Check whether a product requires an Rx case. Storefronts call this
   * before rendering the intake form so they only ask the patient
   * questions when really necessary.
   *
   * Read-only / public — does not require any ownership proof.
   */
  async isRequiredForProduct(
    productId: string,
    opts?: { clinicalNetworkId?: string },
  ): Promise<RxRequiredForProduct> {
    return this.http.get<RxRequiredForProduct>('/api/public/v1/rx/required-for-product', {
      params: {
        productId,
        clinicalNetworkId: opts?.clinicalNetworkId,
      },
    });
  }

  /**
   * Look up the Rx cases attached to an order. Storefronts poll this
   * on the thank-you page to render "Your prescription is being
   * reviewed" → "Shipped" transitions.
   */
  async getCasesForOrder(input: {
    orderId: string;
    checkoutToken: string;
  }): Promise<{ cases: RxCaseSummary[] }> {
    return this.http.post<{ cases: RxCaseSummary[] }>(
      '/api/public/v1/rx/cases/lookup',
      input,
    );
  }

  /**
   * Resume an unfinished intake from `(orderId, checkoutToken)` alone: the
   * patient paid (card on hold) but the identity collected at checkout is
   * gone (tab closed, recovery email link, other device). Returns the Rx
   * line (`productId`, `variantId`) and the basics on the order; ask date of
   * birth and sex at birth again, render the questionnaire, then
   * `submitCase`. `{ caseExists: true }` means a case is already in: show
   * `getCasesForOrder`. Fails with 401 on a wrong token, 404 when the order
   * has no Rx line.
   */
  async getIntakeContext(input: { orderId: string; checkoutToken: string }): Promise<RxIntakeContextResult> {
    return this.http.post<RxIntakeContextResult>('/api/public/v1/rx/intake/context', input);
  }

  /**
   * Submit a fresh clinical case for a freshly-paid order. PHI is
   * forwarded to the clinical network and never persisted on
   * Tagada-side rows.
   */
  async submitCase(input: RxSubmitCaseInput): Promise<RxSubmitCaseResult> {
    return this.http.post<RxSubmitCaseResult>('/api/public/v1/rx/cases/submit', input);
  }

  /**
   * Check the patient (and, once filled, the intake) against the clinical
   * network rules before paying / submitting: US address in the 50 states
   * or DC, US phone, 5 digit ZIP, adult, every active required question
   * answered with an offered option. `submitCase` enforces the same rules
   * and fails with 422 otherwise. Nothing is created.
   */
  async validateIntake(input: RxValidateIntakeInput): Promise<{ valid: boolean; issues: RxIntakeIssue[] }> {
    return this.http.post<{ valid: boolean; issues: RxIntakeIssue[] }>('/api/public/v1/rx/intake/validate', input);
  }

  /**
   * Fetch the network-authored intake questions for a product. Render
   * every question for which `isQuestionActive` is true, build the payload
   * with `toIntakeAnswers`. Empty `questions` means the product has no
   * clinical questionnaire: never replace a network questionnaire with your own.
   */
  async getQuestionsForProduct(
    productId: string,
    variantId?: string,
  ): Promise<RxQuestionsForProduct> {
    return this.http.get<RxQuestionsForProduct>('/api/public/v1/rx/questions-for-product', {
      params: variantId ? { productId, variantId } : { productId },
    });
  }
}
